import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '../../lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

/** Radix menus portaled to document.body sit outside DialogContent but must stay interactive. */
const getOutsideInteractionTarget = (
  event: CustomEvent<{ originalEvent: PointerEvent | FocusEvent }>,
) => {
  const originalTarget = event.detail?.originalEvent?.target;
  if (originalTarget instanceof Element) return originalTarget;
  if (event.target instanceof Element) return event.target;
  return null;
};

const isPortaledOverlayTarget = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return false;

  return Boolean(
    target.closest('[data-nested-dialog]') ||
      target.closest('[data-radix-select-content]') ||
      target.closest('[data-radix-select-viewport]') ||
      target.closest('[data-radix-select-item]') ||
      target.closest('[role="listbox"]') ||
      target.closest('[role="option"]') ||
      target.closest('[data-radix-popper-content-wrapper]') ||
      target.closest('[data-radix-dropdown-menu-content]') ||
      target.closest('[data-radix-popover-content]') ||
      target.closest('[cmdk-root]') ||
      target.closest('[cmdk-item]') ||
      target.closest('[data-radix-alert-dialog-content]') ||
      target.closest('[role="alertdialog"]') ||
      target.closest('[data-agent-share-user-picker]'),
  );
};

const preventIfPortaledOverlay = (
  event: CustomEvent<{ originalEvent: PointerEvent | FocusEvent }>,
) => {
  if (isPortaledOverlayTarget(getOutsideInteractionTarget(event))) {
    event.preventDefault();
  }
};

type DialogOverlayProps = {
  showOverlay?: boolean;
  nested?: boolean;
};

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & DialogOverlayProps
>(({ className, showOverlay = true, nested = false, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      nested ? 'z-[120] bg-black/40 backdrop-blur-[2px]' : 'z-[100] bg-black/50 backdrop-blur-[2px]',
      !showOverlay && 'bg-transparent backdrop-blur-none',
      className
    )}
    {...props}
    data-nested-dialog={nested ? '' : undefined}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  hideClose?: boolean;
  showOverlay?: boolean;
  /** Use when opening a dialog inside another dialog (e.g. Edit profile in Settings). */
  nested?: boolean;
  overlayClassName?: string;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      hideClose = false,
      showOverlay = true,
      nested = false,
      overlayClassName,
      onPointerDownOutside,
      onInteractOutside,
      onFocusOutside,
      onCloseAutoFocus,
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      <DialogOverlay showOverlay={showOverlay} nested={nested} className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-[50%] top-[50%] z-[110] grid w-full max-w-lg max-h-[90dvh] translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto overscroll-contain touch-pan-y rounded-[10px] border border-[#e7e7e7] bg-background p-5 shadow-lg duration-200 dark:border-black-500 dark:bg-card data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
          nested && 'z-[130]',
          className
        )}
        {...props}
        data-nested-dialog={nested ? '' : undefined}
        onPointerDownOutside={(event) => {
          preventIfPortaledOverlay(event);
          onPointerDownOutside?.(event);
        }}
        onInteractOutside={(event) => {
          preventIfPortaledOverlay(event);
          onInteractOutside?.(event);
        }}
        onFocusOutside={(event) => {
          preventIfPortaledOverlay(event);
          onFocusOutside?.(event);
        }}
        onCloseAutoFocus={(event) => {
          if (nested) {
            event.preventDefault();
          }
          onCloseAutoFocus?.(event);
        }}
      >
        {children}
        {!hideClose && (
          <DialogPrimitive.Close className="absolute right-4 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-sm bg-background opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 pr-14 text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
